import React, { createContext, useMemo, useState, useContext, useEffect } from 'react';
import { useRemoveAppLoader } from '@frontend/document';
import { useTranslation } from '@frontend/i18n';
import { ContentLoader } from '@frontend/design-system';
import { INITIAL_RESPONSE } from '../../../constants';
import { useModifiedDentalCoverageResponse } from '../../../helpers';
import { useInsuranceEligibilityReportContainerContext } from '../../../providers/insurance-report-container.provider';
import { Filters, InsuranceReportContextState } from '../../../types';

interface InsuranceContextProviderProps {
  children: React.ReactNode;
}

type insuranceContextType = InsuranceReportContextState & {
  selectedNetworkFilterValue?: string;
  setFilters: (payload: Filters) => void;
};

export const InsuranceReportContext = createContext<insuranceContextType>({
  ...INITIAL_RESPONSE,
  showNetworkFilter: true,
  setFilters: () => {},
});

export const InsuranceReportContextProvider = React.memo(({ children }: InsuranceContextProviderProps) => {
  const { onApiFetchFailure, personId, locationId } = useInsuranceEligibilityReportContainerContext();

  const [filters, setFilters] = useState<Filters>(INITIAL_RESPONSE.filters);
  const { t } = useTranslation('insuranceEligibilityReport');
  const { removeAppLoader } = useRemoveAppLoader({ removeOnMount: false });

  const response = useModifiedDentalCoverageResponse(personId, locationId, onApiFetchFailure);

  const selectedNetworkFilterValue = filters.inNetworkSelected ? t('In Network') : t('Out of Network');

  const providerValue = useMemo(() => {
    return {
      ...response,
      filters,
      personId,
      setFilters,
      selectedNetworkFilterValue,
    };
  }, [response, filters, personId, setFilters, selectedNetworkFilterValue]);

  const isLoading = response.isLoading;

  // Removing the app loader once data is loaded
  useEffect(() => {
    if (!isLoading) {
      // Removing the app loader once data is loaded
      removeAppLoader();
    }
  }, [isLoading]);

  return (
    <InsuranceReportContext.Provider value={providerValue}>
      <ContentLoader show={isLoading} message={t('Loading Insurance Data')} />
      {!isLoading && children}
    </InsuranceReportContext.Provider>
  );
});

InsuranceReportContextProvider.displayName = 'InsuranceReportContextProvider';

export const useInsuranceEligibilityReportContext = () => useContext(InsuranceReportContext);
